import { useState, useEffect } from 'react';
import { attendanceService, assemblyService, propertyService } from 'services/api';
import { useAuth } from 'context/AuthContext';
import { poderesPorUnidad, esDelRepresentante, etiquetaUnidad } from './poderes';

export const POR_PAGINA = 50;
// Sin mayúsculas ni tildes: «perez» encuentra «Pérez».
const norm = (t) => String(t ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Lógica de la pantalla (estado, carga y acciones); la página solo pinta.
export default function useAttendances() {
  const { isSuperAdmin, isAssemblyAdmin, user, isReadOnly: globalIsReadOnly, getSubscriptionStateForProperty } = useAuth();
  const [items,      setItems]      = useState([]);
  const [busqueda,   setBusqueda]   = useState('');
  const [pagina,     setPagina]     = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [alert,      setAlert]      = useState(null);
  const [properties,  setProperties]  = useState([]);
  const [assemblies,  setAssemblies]  = useState([]);
  const [filterProp,       setFilterProp]       = useState('');
  const [filterAsm,        setFilterAsm]        = useState('');
  const [selectedPropObj,  setSelectedPropObj]  = useState(null);

  // isReadOnly efectivo según la propiedad de la asamblea seleccionada
  const selectedAsm_   = assemblies.find(a => String(a.id) === String(filterAsm));
  const selectedPropForSub = selectedAsm_?.property || selectedPropObj;
  const isReadOnly = selectedPropForSub
    ? getSubscriptionStateForProperty(selectedPropForSub) === 'grace'
    : globalIsReadOnly;
  // Asamblea finalizada: la asistencia es la del acta; el servidor rechaza
  // marcar, eliminar o cambiar poderes (para corregir se reabre la asamblea).
  const asmFinalizada = selectedAsm_?.status === 'finalizada';
  // P1: en período de gracia solo se consulta, salvo la asamblea que ya estaba
  // en curso (asistencia y poderes, para poder terminarla con validez).
  const asmEnCurso    = selectedAsm_?.status === 'en_progreso';
  const sinAcciones   = (isReadOnly && !asmEnCurso) || asmFinalizada;
  const selectedPropBlocked = selectedPropForSub
    ? getSubscriptionStateForProperty(selectedPropForSub) === 'blocked'
    : false;
  const [loadingAsm, setLoadingAsm] = useState(true);
  const [repModal,   setRepModal]   = useState(null); // fila de quien OTORGA el poder (ver / externo)
  const [representaModal, setRepresentaModal] = useState(null); // fila de quien REPRESENTA (Representa a…)
  const [darPoderModal,   setDarPoderModal]   = useState(null); // fila de quien ENTREGA el poder (Dar poder a…)
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const [delError,   setDelError]   = useState('');

  useEffect(() => {
    if (isAssemblyAdmin && user?.assembly_id) {
      // assembly_admin: cargar directamente su asamblea asignada sin selector
      const asmId = String(user.assembly_id);
      setFilterAsm(asmId);
      loadAttendances(asmId);
      setLoadingAsm(false);
      return;
    }
    // admin / super_admin: cargar sus propiedades para el selector
    propertyService.selector({ limit: 200 }).then(r => {
      const props = r.data.data || [];
      setProperties(props);
      // Una sola propiedad → se auto-selecciona y se cargan sus asambleas.
      // Varias (o super_admin) → hay que elegir propiedad primero.
      if (props.length === 1) {
        setFilterProp(String(props[0].id));
        setSelectedPropObj(props[0]);
        assemblyService.getAll({ property_id: props[0].id, limit: 200 }).then(r2 => {
          const list = r2.data.data || [];
          setAssemblies(list);
          const active = list.find(a => a.status === 'en_progreso');
          if (active) { setFilterAsm(String(active.id)); loadAttendances(active.id); }
          setLoadingAsm(false);
        }).catch(() => setLoadingAsm(false));
      } else {
        setLoadingAsm(false);
      }
    }).catch(() => setLoadingAsm(false));
  }, [isAssemblyAdmin, user?.assembly_id]); // eslint-disable-line

  const parseAttendances = (data) => setItems(data);

  const loadAttendances = (asmId, silent = false) => {
    if (!asmId) return;
    if (!silent) setLoading(true);
    attendanceService.getAll({ assembly_id: asmId })
      .then(r => { parseAttendances(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  // admin multi-propiedad / super_admin: al elegir propiedad se cargan sus
  // asambleas (misma regla que en Preguntas y Comunicados).
  const handleFilterProp = (propId, elegida) => {
    setFilterProp(propId);
    setFilterAsm('');
    setItems([]);
    setAssemblies([]);
    setSelectedPropObj(elegida || properties.find(p => String(p.id) === String(propId)) || null);
    if (!propId) return;
    setLoadingAsm(true);
    assemblyService.getAll({ property_id: propId, limit: 200 })
      .then(r => {
        const list = r.data.data || [];
        setAssemblies(list);
        const active = list.find(a => a.status === 'en_progreso');
        if (active) { setFilterAsm(String(active.id)); loadAttendances(active.id); }
        setLoadingAsm(false);
      })
      .catch(() => setLoadingAsm(false));
  };

  const handleFilterAsm = (id) => {
    setFilterAsm(id); setItems([]); setBusqueda(''); setPagina(1); loadAttendances(id);
  };

  // Refresco silencioso cada 20s mientras la asamblea seleccionada está en
  // curso — así "presentes ahora" / "retirados" / quórum en vivo se mantienen
  // al día sin recargar la página.
  useEffect(() => {
    const asm = assemblies.find(a => String(a.id) === String(filterAsm));
    if (!filterAsm || asm?.status !== 'en_progreso') return;
    const t = setInterval(() => loadAttendances(filterAsm, true), 20000);
    return () => clearInterval(t);
  }, [filterAsm, assemblies]); // eslint-disable-line

  // F-25: el externo ya no queda presente al registrar su poder. En presencial
  // puede llegar sin la app: el staff lo marca aquí (el servidor marca todos
  // sus poderes en la asamblea).
  const toggleExterno = async (poder) => {
    const newStatus = pstate(poder) === 'presente' ? 'absent' : 'present';
    try {
      await attendanceService.update(poder.id, { status: newStatus });
      setAlert({ type: 'success', message: `${poder.rep_nombre || 'Representante'} marcado como ${newStatus === 'present' ? 'presente' : 'ausente'}` });
      loadAttendances(filterAsm, true);
    } catch (e) {
      setAlert({ type: 'danger', message: e.response?.data?.message || 'Error al actualizar' });
    }
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === 'present' ? 'absent' : 'present';
    // Actualización optimista — el cambio se refleja inmediatamente sin salto
    setItems(prev => prev.map(a =>
      a.id === item.id
        ? {
            ...a, status: newStatus,
            // "Ausente" conserva la hora de ingreso y pone la salida (queda
            // "retirado", como "Salir"); quien nunca entró queda "ausente".
            entry_time:    newStatus === 'present' ? (a.entry_time || new Date().toISOString()) : a.entry_time,
            last_seen_at:  newStatus === 'present' ? new Date().toISOString() : null,
            exit_time:     newStatus === 'present' || !a.entry_time ? null : new Date().toISOString(),
            presence_state: newStatus === 'present' ? 'presente' : (a.entry_time ? 'retirado' : 'ausente'),
            hora_ingreso:  newStatus === 'present' ? (a.hora_ingreso || new Date().toISOString()) : a.hora_ingreso,
            hora_salida:   newStatus === 'present' || !a.hora_ingreso ? null : new Date().toISOString(),
          }
        : a
    ));
    try {
      await attendanceService.update(item.id, { status: newStatus });
      setAlert({ type: 'success', message: `Marcado como ${newStatus === 'present' ? 'presente' : 'ausente'}` });
      // Refresh silencioso para sincronizar con el servidor
      loadAttendances(filterAsm, true);
    } catch {
      // Revertir si falla
      setItems(prev => prev.map(a => a.id === item.id ? item : a));
      setAlert({ type: 'danger', message: 'Error al actualizar' });
    }
  };

  const del = async () => {
    if (!confirmDel) return;
    const id = confirmDel.id;
    setDeleting(true);
    setDelError('');
    setItems(prev => prev.filter(a => a.id !== id));
    try {
      await attendanceService.delete(id);
      setAlert({ type: 'success', message: 'Registro eliminado' });
      setConfirmDel(null);
    } catch (err) {
      setDelError(err.response?.data?.message || 'No se pudo eliminar. Inténtalo de nuevo.');
      loadAttendances(filterAsm, true); // restaurar si falla
    } finally {
      setDeleting(false);
    }
  };

  // keepOpen: "Representa a…" con algún poder fallido — se refresca la tabla
  // pero la ventana sigue abierta mostrando cuáles no quedaron.
  // aviso (F-06): alguna unidad del poder ya había votado con su propietario —
  // se muestra en ámbar junto al mensaje de éxito.
  const onRepSaved = (msg, keepOpen = false, aviso = null) => {
    if (msg) setAlert(aviso ? { type: 'warning', message: `${msg} ${aviso}` } : { type: 'success', message: msg });
    if (!keepOpen) { setRepModal(null); setRepresentaModal(null); setDarPoderModal(null); }
    loadAttendances(filterAsm, true); // silencioso — sin spinner ni salto
  };

  // Poderes (lógica compartida en ./poderes.js): qué poder cubre cada unidad,
  // y a quién representa cada propietario — se muestra una sola vez, en la
  // primera fila de ese propietario.
  const poderPorUnidad = poderesPorUnidad(items);
  const primeraFilaDe = new Map();
  items.forEach(i => { if (i.owner && (!primeraFilaDe.has(i.owner.id) || i.id < primeraFilaDe.get(i.owner.id))) primeraFilaDe.set(i.owner.id, i.id); });
  const representaDe = (item) => {
    if (!item.owner || primeraFilaDe.get(item.owner.id) !== item.id) return [];
    return items.filter(r => esDelRepresentante(poderPorUnidad.get(Number(r.unit_id)), item.owner.numero_identificacion)).map(r => r.unit_id);
  };
  const numeroUnidad = (unitId) => etiquetaUnidad(items.find(i => Number(i.unit_id) === Number(unitId))) || unitId;
  const filasDe = (item) => item.owner ? items.filter(i => i.owner?.id === item.owner.id) : [item];

  const selectedAsm   = assemblies.find(a => String(a.id) === String(filterAsm));
  // super_admin y admin multi-propiedad eligen propiedad primero; admin con 1
  // propiedad y assembly_admin no ven el selector.
  const needsPropSelect = !isAssemblyAdmin && (isSuperAdmin || properties.length > 1);
  const showAsmSelect   = !isAssemblyAdmin && (!needsPropSelect || !!filterProp);
  const selectedPropName = filterProp
    ? (selectedPropObj?.name || properties.find(p => String(p.id) === String(filterProp))?.name || '')
    : '';
  // presence_state ('presente' | 'retirado' | 'ausente') lo calcula el backend
  // a partir del último latido; si falta (dato viejo), se cae a status.
  const pstate = (i) => i.presence_state || (i.status === 'present' ? 'presente' : 'ausente');
  // Solo se corta lo que se PINTA: contadores, poderes y modales siguen usando la lista completa.
  const q = norm(busqueda).trim();
  const coincide = (i) => !q || [
    i.owner && `${i.owner.nombre} ${i.owner.apellido}`, i.owner?.numero_identificacion, i.rep_nombre, i.rep_numero_documento,
    i.unit?.numero_unidad, i.unit?.building?.numero_torre,
  ].some(v => norm(v).includes(q));
  const filasFiltradas = items.filter(coincide);
  const paginaActual   = Math.min(pagina, Math.max(1, Math.ceil(filasFiltradas.length / POR_PAGINA)));
  const filasPagina    = filasFiltradas.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA);
  const irAPagina      = (n) => { setPagina(n); window.scrollTo({ top: 0 }); };

  const totalPresent  = items.filter(i => pstate(i) === 'presente').length;
  const totalRetirado = items.filter(i => pstate(i) === 'retirado').length;
  const totalRep      = items.filter(i => i.attendance_type === 'representative').length;
  const totalAbsent   = items.filter(i => pstate(i) === 'ausente').length;
  const pctPresent    = items.length ? Math.round((totalPresent / items.length) * 100) : 0;

  const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';

  return {
    items, busqueda, setBusqueda, setPagina, loading, alert, setAlert, assemblies, filterProp,
    filterAsm, loadingAsm, repModal, setRepModal, representaModal, setRepresentaModal, darPoderModal,
    setDarPoderModal, confirmDel, setConfirmDel, deleting, delError, setDelError, isAssemblyAdmin, 
    selectedAsm_, asmFinalizada, sinAcciones, selectedPropBlocked, handleFilterProp, handleFilterAsm,
    toggleExterno, toggleStatus, del, onRepSaved, poderPorUnidad, representaDe, numeroUnidad, filasDe,
    selectedAsm, needsPropSelect, showAsmSelect, selectedPropName, pstate, filasFiltradas, paginaActual,
    filasPagina, irAPagina, totalPresent, totalRetirado, totalRep, totalAbsent, pctPresent, BASE_URL,
  };
}
